import React from 'react';
import { CheckCircleIcon, KeyIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import JourneyBottle from './JourneyBottle';
import { BottleState, formatRecordingDuration, JourneyStep } from './journeySteps';

type JourneySceneProps = {
  step: JourneyStep;
  index: number;
  bottleState: BottleState;
  unlocked?: boolean;
  assigning: boolean;
  recording: boolean;
  handingOver: boolean;
  filling: boolean;
  locking: boolean;
  unlocking: boolean;
  recordingSeconds: number;
  milkType: string;
  quantity: number;
  deliverySlots: string[];
  language?: 'en' | 'hi';
  onMilkTypeChange: (milkType: string) => void;
  onQuantityChange: (quantity: number) => void;
  onDeliverySlotsChange: (deliverySlots: string[]) => void;
  onAction: () => void;
  actionLabel?: string;
  actionDisabled?: boolean;
  successMessage?: string;
};

const milkPricesEn: Record<string, number> = { 'A1 Cow Milk': 63, 'A2 Desi Cow Milk': 87, 'A2 Buffalo Milk': 91 };
const milkNamesHi: Record<string, string> = {
  'A1 Cow Milk': 'गाय का दूध',
  'A2 Desi Cow Milk': 'देसी गाय A2 दूध',
  'A2 Buffalo Milk': 'A2 भैंस का दूध',
};

const sceneUi = {
  en: {
    kicker: 'Follow Your Milk',
    unlocked: '🔓 Bottle #7 Unlocked',
    unlockedBtn: '✓ Bottle #7 Unlocked',
    perDelivery: 'per delivery',
    proofItems: ['Subscription linked', 'Milking recorded', 'Filling recorded', 'Bottle locked', 'Bottle traced', 'Cold chain maintained', 'Delivered to you'],
    cues: {
      'farm-logistics': 'Collection → Farm',
      'cold-chain': '2–4°C • Cold chain',
    },
    bucketLabel: 'STAINLESS STEEL BUCKET',
    recording: 'REC',
    ready: 'READY',
    milkingVideo: 'MILKING VIDEO',
    litres: 'L',
    decreaseQty: 'Decrease quantity',
    increaseQty: 'Increase quantity',
  },
  hi: {
    kicker: 'अपने दूध का सफ़र देखें',
    unlocked: '🔓 बोतल #7 खुली',
    unlockedBtn: '✓ बोतल #7 खुली',
    perDelivery: 'प्रति डिलीवरी',
    proofItems: ['सदस्यता जुड़ी', 'दुहाई रिकॉर्ड', 'भरना रिकॉर्ड', 'बोतल बंद', 'बोतल ट्रेस', 'कोल्ड चेन बनाई', 'आपको डिलीवर'],
    cues: {
      'farm-logistics': 'संग्रह → फार्म',
      'cold-chain': '2–4°C • कोल्ड चेन',
    },
    bucketLabel: 'स्टेनलेस स्टील बाल्टी',
    recording: 'रिकॉर्ड',
    ready: 'तैयार',
    milkingVideo: 'दुहाई वीडियो',
    litres: 'लीटर',
    decreaseQty: 'मात्रा घटाएं',
    increaseQty: 'मात्रा बढ़ाएं',
  },
};

const MilkingSceneArt = () => (
  <>
    <ellipse cx="108" cy="101" rx="77" ry="44" fill="#4b4036" /><path d="M169 80l42-20 23 24-39 37Z" fill="#40362e" /><circle cx="218" cy="78" r="4" fill="#f8f6f1" />
    <path d="M52 133v48m34-36v38m52-38v38m28-48v46" stroke="#372f29" strokeWidth="12" strokeLinecap="round" />
    <ellipse cx="113" cy="142" rx="19" ry="12" fill="#d6a48b" /><path d="M105 150l-7 20m22-20 8 20" stroke="#d6a48b" strokeWidth="5" strokeLinecap="round" />
    <g transform="translate(-10 0)">
  <circle cx="70" cy="140" r="18" fill="#8f5e40" />

  <path
    d="M54 165q16-22 33 0l12 47H43Z"
    fill="#1b5e20"
  />

  <path
    d="M79 175l25 1 9 2"
    stroke="#8f5e40"
    strokeWidth="8"
    strokeLinecap="round"
  />

  <circle cx="113" cy="178" r="5" fill="#8f5e40" />
</g>
    <path d="M98 190h40l-6 22h-28Z" fill="#9ba7a8" stroke="#5c6968" strokeWidth="4" />
    {[0, 1, 2].map((drop) => <motion.circle key={drop} cx="128" cy="173" r="4" fill="#fffdf5" animate={{ cy: [173, 197], opacity: [0, 1, 0] }} transition={{ duration: 0.8, delay: drop * 0.25, repeat: Infinity, repeatDelay: 0.2 }} />)}
  </>
);

const FillingSceneArt = ({ active = true }: { active?: boolean }) => (
  <>
    <g transform="translate(20 -15) scale(1.1)">
      <motion.g animate={{ rotate: active ? [0, 18, 18, 0] : 0 }} transition={active ? { duration: 2.8, repeat: Infinity, repeatDelay: 0.4 } : { duration: 0.2 }} style={{ transformOrigin: '96px 154px' }}>
        <path d="M48 91h112l-15 96H66Z" fill="#c4cdca" stroke="#5b6b69" strokeWidth="5" /><ellipse cx="104" cy="91" rx="56" ry="15" fill="#eef0ea" stroke="#5b6b69" strokeWidth="5" /><ellipse cx="104" cy="90" rx="46" ry="9" fill="#fffdf5" opacity="0.85" /><path d="M68 109h73" stroke="#ffffff" strokeWidth="5" opacity="0.5" /><path d="M153 92l21 10-13 20-11-15Z" fill="#aeb9b6" stroke="#5b6b69" strokeWidth="4" />
      </motion.g>
    </g>
    {active && <path d="M205 96C218 125 215 160 215 195" fill="none" stroke="#fffdf5" strokeWidth="13" strokeLinecap="round" opacity="0.92" />}
    {active && <motion.path d="M205 96C218 125 215 160 215 195" fill="none" stroke="#ffffff" strokeWidth="7" strokeLinecap="round" strokeDasharray="10 9" animate={{ strokeDashoffset: [0, -57] }} transition={{ duration: 0.85, repeat: Infinity, ease: 'linear' }} />}
    {active && [0, 1, 2].map((drop) => <motion.circle key={drop} cx="215" cy="195" r="4" fill="#fffdf5" animate={{ cy: [185, 201], opacity: [0, 1, 0] }} transition={{ duration: 0.65, delay: drop * 0.2, repeat: Infinity }} />)}
  </>
);

type CameraLabels = { recording: string; ready: string; milkingVideo: string };
const instagramReelUrl = 'https://www.instagram.com/reel/DdOiD3eMQdM/embed/';

const RecordingPhone = ({ x, y, scene, recordingTime, labels, playback = false, recording = true, active = true }: { x: number; y: number; scene: 'milking' | 'filling' | 'locking'; recordingTime: string; labels: CameraLabels; playback?: boolean; recording?: boolean; active?: boolean }) => (
  <g transform={`translate(${x} ${y})`} aria-hidden="true">
    <rect width="112" height="78" rx="9" fill="#17251a" stroke="#fff" strokeWidth="2" />
    <rect x="5" y="12" width="102" height="62" rx="5" fill="#dce8d3" />
    {playback ? <text x="10" y="10" fill="#fff" fontFamily="sans-serif" fontSize="8">▶</text> : recording ? <motion.circle cx="14" cy="7" r="3.5" fill="#ef4444" animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 1.1, repeat: Infinity }} /> : <circle cx="14" cy="7" r="3.5" fill="#8b948d" />}
    <text x={playback ? 65 : 92} y="10" textAnchor="middle" fill="#fff" fontFamily="sans-serif" fontSize={playback ? 6.5 : 8} fontWeight="700">{playback ? labels.milkingVideo : recording ? labels.recording : labels.ready}</text>
    <svg x="6" y="25" width="100" height="48" viewBox={scene === 'milking' ? '0 0 340 230' : scene === 'filling' ? '0 0 350 260' : '0 0 800 400'} preserveAspectRatio="none">
      {scene === 'milking' ? <MilkingSceneArt /> : scene === 'filling' ? <FillingSceneArt active={active} /> : <motion.image href="/rightoz-bottle-007.svg" x="80" y="-30" width="640" height="840" preserveAspectRatio="xMidYMin slice" initial={{ y: -18 }} animate={{ y: active ? [-18, 0, -2, 0] : -18 }} transition={{ duration: 1.8, times: [0, 0.62, 0.82, 1], ease: 'easeInOut' }} />}
      {scene === 'filling' ? <image href="/rightoz-bottle-007.svg" x="188" y="165" width="55" height="100" preserveAspectRatio="xMidYMax meet" /> : scene !== 'locking' && <image href="/rightoz-bottle-007.svg" x="280" y="130" width="55" height="100" preserveAspectRatio="xMidYMax meet" />}
    </svg>
    <text x="56" y="24" textAnchor="middle" fill="#17251a" fontFamily="monospace" fontSize="8" fontWeight="700">{recordingTime}</text>
  </g>
);

const MilkingMotion = ({ recordingTime, recording, labels }: { recordingTime: string; recording: boolean; labels: CameraLabels }) => (
  <div className="absolute inset-x-4 bottom-1 top-4">
    <svg viewBox="0 0 340 230" className="h-full w-full" role="img" aria-label="Farmer hand-milking a cow into a stainless-steel bucket">
      <g transform="translate(-30 -60)"><MilkingSceneArt /></g>
      <g aria-hidden="true" fill="none" stroke="#52605a" strokeLinecap="round" strokeWidth="4">
        <path d="M270 223v12l-24 27m24-27 24 27m-24-27v30" />
      </g>
      <RecordingPhone x={230} y={145} scene="milking" recordingTime={recordingTime} recording={recording} labels={labels} />
    </svg>
    <JourneyBottle state="empty" className="absolute bottom-6 right-[49%] w-16 drop-shadow-lg sm:w-20" />
  </div>
);

const InstagramPhone = () => (
  <div className="absolute right-5 top-[23%] w-[38%] max-w-[190px] rounded-xl border-[3px] border-white bg-[#17251a] p-1.5 shadow-[0_14px_18px_rgba(18,37,26,0.28)]">
    <div className="relative aspect-[82/100] overflow-hidden rounded-lg bg-[#dce8d3]">
      <div className="absolute h-[620px] origin-top-left" style={{ left: '-23.5%', top: -24, width: '238.095%', transform: 'scale(0.42) scaleX(1.47)' }}>
        <iframe title="RightOZ Instagram reel" src={instagramReelUrl} loading="lazy" className="h-full w-full border-0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" />
      </div>
    </div>
  </div>
);

const EnjoyMotion = () => (
  <div className="absolute inset-x-3 bottom-[-8px] top-4" role="img" aria-label="Open Bottle #7 beside a phone showing the RightOZ Instagram reel">
    <JourneyBottle state="unlocked" capRemoved milkVisible className="absolute bottom-0 left-0 w-[58%] max-w-[230px] drop-shadow-[0_24px_22px_rgba(27,94,32,0.22)]" />
    <InstagramPhone />
  </div>
);

const FillingMotion = ({ recordingTime, active, labels }: { recordingTime: string; active: boolean; labels: CameraLabels }) => (
  <div className="absolute inset-x-2 bottom-[-4px] top-1 sm:inset-x-4 sm:top-2">
    <svg viewBox="0 0 350 260" className="relative z-10 h-full w-full" role="img" aria-label="Fresh milk moving from a stainless-steel bucket into the open neck of Bottle #7">
      <g transform="translate(-35 -42)"><FillingSceneArt active={active} /></g>
      <g aria-hidden="true" fill="none" stroke="#52605a" strokeLinecap="round" strokeWidth="4">
        <path d="M282 222v12l-25 25m25-25 25 25m-25-25v26" />
      </g>
      <RecordingPhone x={226} y={144} scene="filling" recordingTime={recordingTime} active={active} labels={labels} />
    </svg>
    <JourneyBottle state={active ? 'filled' : 'empty'} side="back" compactBack capRemoved milkVisible={active} digitsClassName="top-[37%]" className="absolute bottom-[5%] right-[29%] w-28 sm:w-36 md:w-40 drop-shadow-[0_20px_20px_rgba(27,94,32,0.22)]" />
  </div>
);

const LockingBottleArt = ({ className = '', active }: { className?: string; active: boolean }) => (
  <div className={`relative aspect-[800/1050] ${className}`}>
    <JourneyBottle state="filled" side="back" compactBack capRemoved digitsClassName="top-[34%]" className="absolute inset-0 w-full drop-shadow-[0_24px_22px_rgba(27,94,32,0.22)]" />
    <span aria-hidden="true" className="absolute left-[30.5%] top-[18.2%] z-[1] h-[5.8%] w-[39%] rounded-[50%] border-[2px] border-[#626d69] bg-[linear-gradient(90deg,#7b8581,#d9ddda_22%,#aab2ae_52%,#eceee9_76%,#737d79)] shadow-[inset_0_2px_2px_rgba(255,255,255,0.45)]" />
    <motion.div
      initial={{ y: '-2%' }}
      animate={{ y: active ? ['-2%', '2%', '1.5%', '2%'] : '-2%' }}
      transition={{ duration: 1.8, times: [0, 0.62, 0.82, 1], ease: 'easeInOut' }}
      className="pointer-events-none absolute inset-x-0 top-0 z-[2]"
    >
      <div className="relative aspect-[800/1050] w-full [container-type:inline-size]">
        <img src="/rightoz-cap-base.svg" alt="" className="pointer-events-none absolute inset-x-0 top-0 h-auto w-full" />
        <motion.span
          aria-hidden="true"
          initial={{ rotate: 90 }}
          animate={{ rotate: active ? [90, 90, 0, 0] : 90 }}
          transition={{ duration: 1.8, times: [0, 0.62, 0.9, 1], ease: 'easeInOut' }}
          style={{ transformOrigin: '92% 50%' }}
          className="absolute left-[28.75%] top-[10.14%] h-[6.29%] w-[24.25%] rounded-full border-[3px] border-[#0a4f2c] bg-[#147d48] shadow-[0_2px_3px_rgba(0,0,0,0.25)]"
        />
        <span aria-hidden="true" className="absolute left-[62.5%] top-[9.5%] font-sans text-[10cqw] font-black leading-none text-[#202725]">7</span>
      </div>
    </motion.div>
  </div>
);

const LockingMotion = ({ recordingTime, active, recordingLabel }: { recordingTime: string; active: boolean; recordingLabel: string }) => (
  <div className="absolute inset-x-2 bottom-[-8px] top-4">
    <LockingBottleArt active={active} className="absolute bottom-[2%] left-[2%] w-[58%] max-w-[230px]" />
    <div className="absolute bottom-10 right-0 z-20 aspect-[112/78] w-[42%] max-w-[160px] rounded-xl border-[3px] border-white bg-[#17251a] p-1 shadow-[0_10px_22px_rgba(18,37,26,0.3)]">
      <svg aria-hidden="true" viewBox="0 0 80 40" className="pointer-events-none absolute left-1/2 top-[calc(100%-4px)] -z-10 h-10 w-20 -translate-x-1/2 overflow-visible" fill="none" stroke="#52605a" strokeLinecap="round" strokeWidth="4">
        <path d="M40 0v8L15 38m25-30 25 30M40 8v32" />
      </svg>
      <div className="absolute inset-x-1 top-1 flex h-4 items-center justify-between px-1 text-[8px] font-bold text-white">
        <motion.span className="h-2 w-2 rounded-full bg-red-500" animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 1.1, repeat: Infinity }} />
        <span>{recordingLabel}</span>
      </div>
      <div className="absolute inset-x-1 bottom-1 top-5 overflow-hidden rounded-md bg-[#dce8d3]">
        <span className="absolute inset-x-0 top-1 z-10 text-center font-mono text-[9px] font-bold text-[#17251a]">{recordingTime}</span>
        <LockingBottleArt active={active} className="absolute bottom-[-15%] left-1/2 w-[58%] -translate-x-1/2" />
      </div>
    </div>
  </div>
);

const JourneyScene: React.FC<JourneySceneProps> = ({
  step,
  index,
  bottleState,
  unlocked = false,
  assigning,
  recording,
  handingOver,
  filling,
  locking,
  unlocking,
  recordingSeconds,
  milkType,
  quantity,
  deliverySlots,
  language = 'en',
  onMilkTypeChange,
  onQuantityChange,
  onDeliverySlotsChange,
  onAction,
  actionLabel,
  actionDisabled = false,
  successMessage,
}) => {
  const recTime = formatRecordingDuration(recordingSeconds);
  const t = sceneUi[language];
  const cameraLabels = { recording: t.recording, ready: t.ready, milkingVideo: t.milkingVideo };
  const icon = step.id === 'farm-logistics' || step.id === 'delivery' ? <MapPinIcon className="h-8 w-8" /> : step.id === 'unlock' ? <KeyIcon className="h-8 w-8" /> : <CheckCircleIcon className="h-8 w-8" />;
  const processMotion = step.id === 'milking' ? <MilkingMotion recordingTime={recTime} recording={recording} labels={cameraLabels} /> : step.id === 'filling' ? <FillingMotion recordingTime={recTime} active={filling} labels={cameraLabels} /> : step.id === 'locking' ? <LockingMotion recordingTime={recTime} active={locking} recordingLabel={t.recording} /> : step.id === 'enjoy' ? <EnjoyMotion /> : null;
  const showRecordingBanner = recording && !['milking', 'filling', 'locking'].includes(step.id);
  const isUnlockStep = step.id === 'unlock';
  const isTransitStep = ['farm-logistics', 'cold-chain', 'delivery'].includes(step.id);
  const cue = t.cues[step.id as keyof typeof t.cues];

  // For Step 1: show milk names in the user's language
  const milkOptions = Object.keys(milkPricesEn);

  return (
    <motion.div key={step.id} initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.28 }} className="px-5 py-7 sm:px-8 md:py-10">
      <p className="mb-6 text-center text-base font-semibold leading-snug text-[var(--color-forest)] sm:text-lg">{step.description}</p>
      <div className={`grid gap-7 md:items-center md:gap-12 ${step.id === 'subscribe' ? 'md:grid-cols-1' : 'md:grid-cols-[minmax(0,0.9fr)_minmax(19rem,1.1fr)]'}`}>
      {step.id !== 'subscribe' && <div className="relative min-h-[290px] overflow-hidden rounded-[2rem] bg-[radial-gradient(circle_at_20%_15%,rgba(76,175,80,0.2),transparent_30%),linear-gradient(150deg,#eff4e9,#dce8d3)] p-5 sm:min-h-[360px]">
        <div className="absolute left-5 top-5 z-10 text-[var(--color-leaf)]">{icon}</div>
        {cue && <p className="absolute right-4 top-4 z-10 max-w-[70%] rounded-full bg-white/90 px-3 py-2 text-right text-[11px] font-bold text-[var(--color-forest)] shadow-sm sm:right-5 sm:top-5 sm:text-xs">{cue}</p>}
        {showRecordingBanner && <div className="absolute right-5 top-5 z-10 flex items-center gap-2 rounded-full bg-[#17251a] px-3 py-2 text-xs font-bold text-white"><span className="h-2 w-2 animate-pulse rounded-full bg-red-500 motion-reduce:animate-none" /> {t.recording} {recTime}</div>}
        {processMotion || (step.id === 'allotment' ? (
          <div className="absolute inset-x-3 bottom-[-8px] top-4">
            <motion.div initial={{ opacity: 0, x: -28 }} animate={assigning ? { opacity: 1, x: 0, scale: [1, 1.05, 1] } : { opacity: 0, x: -28 }} transition={{ duration: 0.55, ease: 'easeOut' }} className="absolute bottom-0 left-[14%] w-[55%]">
              <JourneyBottle state={bottleState} side="front" showKey={assigning} className="w-full drop-shadow-[0_20px_18px_rgba(27,94,32,0.22)]" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 28 }} animate={assigning ? { opacity: 1, x: 0, scale: [1, 1.05, 1] } : { opacity: 0, x: 28 }} transition={{ duration: 0.55, ease: 'easeOut' }} className="absolute bottom-0 right-0 w-[55%]">
              <JourneyBottle state={bottleState} side="back" className="w-full drop-shadow-[0_20px_18px_rgba(27,94,32,0.22)]" />
            </motion.div>
          </div>
        ) : (
          <motion.div
            initial={false}
            animate={step.id === 'collection' && handingOver
              ? { x: [0, 70], opacity: [1, 0] }
              : isTransitStep
                ? { x: [-22, 22, -22], y: [0, -4, 0], opacity: 1 }
                : { x: 0, y: 0, opacity: 1 }}
            transition={step.id === 'collection' && handingOver
              ? { duration: 1.45, ease: 'easeInOut' }
              : { duration: 2.4, repeat: Infinity, repeatType: 'mirror' }}
            className={`absolute inset-x-0 bottom-[-8px] mx-auto ${isUnlockStep ? 'w-[180px] sm:w-[210px]' : 'w-[240px] sm:w-[280px]'}`}
          >
            {step.id === 'collection' ? (
              <div className="relative">
                <motion.div
                  animate={{ opacity: [1, 0.35, 0, 0.35, 1], scaleX: [1, 0.72, 0.48, 0.72, 1], x: [0, -10, -14, 10, 0] }}
                  transition={{ duration: 7, ease: 'easeInOut', repeat: Infinity, times: [0, 0.25, 0.5, 0.75, 1] }}
                >
                  <JourneyBottle state={bottleState} side="front" className="h-auto w-full drop-shadow-[0_24px_22px_rgba(27,94,32,0.22)]" />
                </motion.div>
                <motion.div
                  animate={{ opacity: [0, 0.35, 1, 0.35, 0], scaleX: [0.48, 0.72, 1, 0.72, 0.48], x: [-14, -10, 0, 10, 14] }}
                  transition={{ duration: 7, ease: 'easeInOut', repeat: Infinity, times: [0, 0.25, 0.5, 0.75, 1] }}
                  className="absolute inset-0"
                >
                  <JourneyBottle state={bottleState} side="back" className="h-auto w-full drop-shadow-[0_24px_22px_rgba(27,94,32,0.22)]" />
                </motion.div>
              </div>
            ) : (
              <JourneyBottle
                state={bottleState}
                side={step.id === 'traceability' ? 'back' : 'front'}
                capOpen={isUnlockStep && unlocked}
                unlocking={isUnlockStep && (unlocking || unlocked)}
                capRemoved={step.id === 'enjoy'}
                milkVisible={step.id === 'enjoy' || (isUnlockStep && (unlocking || unlocked))}
                showKey={isUnlockStep}
                keyTurned={unlocking || unlocked}
                interactiveKey={isUnlockStep && !unlocking && !unlocked}
                onKeyClick={onAction}
                className="h-auto w-full drop-shadow-[0_24px_22px_rgba(27,94,32,0.22)]"
              />
            )}
          </motion.div>
        ))}
      </div>}
      <div className={step.id === 'subscribe' ? 'mx-auto w-full max-w-2xl' : ''}>
        <p className="section-kicker">{t.kicker}</p>
        <h2 className="section-title text-[var(--color-ink)]">{index + 1}. {step.title}</h2>
        {step.detail && <p className="mt-4 text-sm font-semibold text-[var(--color-forest)]">{step.detail}</p>}
        {step.id === 'subscribe' && (
          <div className="mt-6 rounded-2xl border border-[rgba(27,94,32,0.12)] bg-white p-4">
            <div className="flex flex-wrap gap-2">
              {milkOptions.map((milk) => (
                <button key={milk} type="button" onClick={() => onMilkTypeChange(milk)} aria-pressed={milkType === milk} className={`rounded-full border px-3 py-2 text-sm font-semibold ${milkType === milk ? 'border-[var(--color-forest)] bg-[var(--color-forest)] text-white' : 'border-[rgba(27,94,32,0.15)] text-[var(--color-forest)]'}`}>
                  {language === 'hi' ? milkNamesHi[milk] : milk}
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button type="button" onClick={() => onQuantityChange(Math.max(1, quantity - 1))} aria-label={t.decreaseQty} className="rounded-full border px-3 py-1">−</button>
              <span className="font-semibold">{quantity} {t.litres}</span>
              <button type="button" onClick={() => onQuantityChange(quantity + 1)} aria-label={t.increaseQty} className="rounded-full border px-3 py-1">+</button>
            </div>
            <div className="mt-4">
              <p className="text-sm font-semibold text-[var(--color-forest)]">{language === 'hi' ? 'डिलीवरी' : 'Delivery'}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {['morning', 'evening'].map((slot) => {
                  const selected = deliverySlots.includes(slot);
                  return <button key={slot} type="button" aria-pressed={selected} onClick={() => onDeliverySlotsChange(selected ? (deliverySlots.length > 1 ? deliverySlots.filter((value) => value !== slot) : deliverySlots) : [...deliverySlots, slot])} className={`rounded-full border px-4 py-2 text-sm font-semibold ${selected ? 'border-[var(--color-forest)] bg-[var(--color-forest)] text-white' : 'border-[rgba(27,94,32,0.15)] text-[var(--color-forest)]'}`}>{language === 'hi' ? (slot === 'morning' ? 'सुबह' : 'शाम') : slot[0].toUpperCase() + slot.slice(1)}</button>;
                })}
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-[var(--color-forest)]">₹{milkPricesEn[milkType]}/L · ₹{milkPricesEn[milkType] * quantity} {t.perDelivery}</p>
          </div>
        )}
        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            disabled={actionDisabled}
            className="mt-6 inline-flex animate-pulse items-center gap-2 rounded-full border-2 border-[#e8ffd9] bg-[linear-gradient(135deg,#c9ff9d,#47ef83_48%,#16c95b)] px-5 py-3 text-sm font-extrabold text-[#062c16] shadow-[0_0_0_5px_rgba(198,255,151,0.9),0_0_28px_rgba(106,255,116,1),0_0_62px_rgba(39,219,76,0.9)] transition hover:brightness-90 motion-reduce:animate-none focus:outline-none focus:ring-4 focus:ring-[rgba(106,255,116,0.5)] disabled:cursor-wait disabled:animate-none disabled:opacity-70"
          >
            {isUnlockStep && unlocked ? t.unlockedBtn : actionLabel}
          </button>
        )}
        {successMessage && (
          <p role="status" className="mt-4 rounded-xl bg-[#edf6e8] px-4 py-3 text-sm font-bold text-[var(--color-forest)]">
            ✓ {successMessage}
          </p>
        )}
        {step.id === 'proof' && (
          <div className="mt-6 grid gap-2 text-sm text-[var(--color-muted)] sm:grid-cols-2">
            {t.proofItems.map((proof) => (
              <span key={proof} className="flex items-center gap-2"><CheckCircleIcon className="h-5 w-5 text-[var(--color-leaf)]" />{proof}</span>
            ))}
          </div>
        )}
      </div>
      </div>
    </motion.div>
  );
};

export default JourneyScene;
